import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import DataTable from "../../components/DataTable";
import { useSnackbar } from "../../context/SnackbarContext";
import { stockService } from "../../services/stock/stockService";
import { userService } from "../../services/user/userService";
import NumbersIcon from "@mui/icons-material/Numbers";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import QrCodeIcon from "@mui/icons-material/QrCode";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PercentIcon from "@mui/icons-material/Percent";
import NotesIcon from "@mui/icons-material/Notes";
import { Society, StockStatusResponse, Product } from "../../types/api.types";
import { societyService } from "../../services/society/societyService";
import { productService } from "../../services/product/productService";

const CODE_WIDTH = 150;

interface StockFormData {
  internalNumber: string;
  ourCorrelative: string;
  estimatedArribalDate: string;
  companyId: string;
  code: string;
  statusId: string;
  percentageSafety: string;
  observation: string;
}

interface ArticleItem {
  id: string;
  nombre: string;
  productId: string;
  code: string;
  cantidad: number;
  resto?: number;
}

interface ArticleFormData {
  nombre: string;
  productId: string;
  code: string;
  cantidad: string;
  resto?: string;
}

const StockCreatePage = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [societies, setSocieties] = useState<Society[]>([]);
  const [statuses, setStatuses] = useState<StockStatusResponse[]>([]);
  const [articles, setArticles] = useState<ArticleItem[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingArticle, setEditingArticle] = useState<ArticleItem | null>(
    null,
  );
  const [articlePage, setArticlePage] = useState<number>(1);
  const [societyInputValue, setSocietyInputValue] = useState<string>("");
  const [loadingSocieties, setLoadingSocieties] = useState<boolean>(false);
  const [societyOpen, setSocietyOpen] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productInputValue, setProductInputValue] = useState<string>("");
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [productOpen, setProductOpen] = useState<boolean>(false);

  const { showSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<StockFormData>({
    defaultValues: {
      internalNumber: "",
      ourCorrelative: "",
      estimatedArribalDate: "",
      companyId: "",
      percentageSafety: "",
      observation: "",
    },
  });

  const {
    control: articleControl,
    handleSubmit: handleArticleSubmit,
    formState: { errors: articleErrors },
    reset: resetArticleForm,
    setValue: setArticleValue,
  } = useForm<ArticleFormData>({
    defaultValues: {
      nombre: "",
      code: "",
      productId: "",
      cantidad: "",
      resto: "",
    },
  });

  // Búsqueda asíncrona de sociedades cada vez que el usuario escribe
  useEffect(() => {
    let active = true;

    if (!societyOpen) {
      return undefined;
    }

    const fetchSocieties = async () => {
      setLoadingSocieties(true);
      try {
        const societiesResponse = await societyService.getSocieties(
          0,
          30,
          societyInputValue,
        );

        if (active) {
          setSocieties((societiesResponse?.companies as any) || []);
        }
      } catch (error: any) {
        if (active) {
          showSnackbar(
            error.response?.data?.message || "Error al buscar sociedades",
            "error",
            4000,
          );
          setSocieties([]);
        }
      } finally {
        if (active) {
          setLoadingSocieties(false);
        }
      }
    };

    // Debounce para evitar demasiadas peticiones
    const delayDebounceFn = setTimeout(() => {
      fetchSocieties();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [societyInputValue, societyOpen, showSnackbar]);

  // Búsqueda asíncrona de productos cada vez que el usuario escribe
  useEffect(() => {
    let active = true;

    if (!productOpen) {
      return undefined;
    }

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const productsResponse = await productService.getProducts(
          0,
          30,
          productInputValue,
        );

        if (active) {
          setProducts(productsResponse?.products || []);
        }
      } catch (error: any) {
        if (active) {
          showSnackbar(
            error.response?.data?.message || "Error al buscar productos",
            "error",
            4000,
          );
          setProducts([]);
        }
      } finally {
        if (active) {
          setLoadingProducts(false);
        }
      }
    };

    // Debounce para evitar demasiadas peticiones
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [productInputValue, productOpen, showSnackbar]);

  const onGetStatuses = async () => {
    const response = await stockService.getStatuses();
    setStatuses(response);
  };

  const onGetSocieties = async () => {
    const response = await userService.getCompanies();
    setSocieties(response as any);
  };

  useEffect(() => {
    onGetStatuses();
  }, []);

  const onSubmit = async (data: StockFormData) => {
    try {
      setIsLoading(true);

      // Validar que haya al menos un artículo
      if (articles.length === 0) {
        showSnackbar(
          "Debe agregar al menos un artículo al stock",
          "warning",
          4000,
        );
        setIsLoading(false);
        return;
      }

      // Construir el body según la estructura requerida
      const body = {
        stockHeader: {
          companyId: data.companyId,
          code: data.code,
          observation: data.observation,
          estimatedArribalDate: data.estimatedArribalDate,
          percentageSafety: Number(data.percentageSafety),
          stockItem: articles.map((article) => ({
            productId: article.productId,
            code: article.code,
            quantity: article.cantidad,
            rest: article.resto,
          })),
        },
      };

      await stockService.createStock(body);
      navigate("/stock");
      showSnackbar("Stock creado correctamente", "success", 3000);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al crear stock",
        "error",
        4000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/stock");
  };

  const handleOpenModal = () => {
    setEditingArticle(null);
    resetArticleForm({
      nombre: "",
      code: "",
      productId: "",
      cantidad: "",
      resto: "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingArticle(null);
    resetArticleForm();
    setProductInputValue("");
  };

  const handleEditArticle = (article: ArticleItem) => {
    setEditingArticle(article);
    resetArticleForm({
      nombre: article.nombre,
      productId: article.productId,
      code: article.code,
      cantidad: article.cantidad.toString(),
      resto: article.resto?.toString() || "",
    });
    setProductInputValue(article.nombre);
    setOpenModal(true);
  };

  const handleDeleteArticle = (id: string) => {
    setArticles((prev) => prev.filter((article) => article.id !== id));
    showSnackbar("Artículo eliminado correctamente", "success", 3000);
  };

  const onArticleSubmit = (data: ArticleFormData) => {
    // Validar que productId esté presente
    if (!data.productId) {
      showSnackbar("Debe seleccionar un producto de la lista", "error", 3000);
      return;
    }
    if (editingArticle) {
      // Editar artículo existente
      setArticles((prev) =>
        prev.map((article) =>
          article.id === editingArticle.id
            ? {
                ...article,
                nombre: data.nombre,
                code: data.code,
                productId: data.productId,
                cantidad: Number(data.cantidad),
                resto: Number(data.resto),
              }
            : article,
        ),
      );
      showSnackbar("Artículo actualizado correctamente", "success", 3000);
    } else {
      const alreadyExists = articles.some(
        (article) => article.code.trim() === data.code.trim(),
      );

      if (alreadyExists) {
        showSnackbar("El producto ya fue agregado al stock", "error", 3000);
        return;
      }

      // Agregar nuevo artículo
      const newArticle: ArticleItem = {
        id: Date.now().toString(),
        nombre: data.nombre,
        code: data.code,
        productId: data.productId,
        cantidad: Number(data.cantidad),
      };
      setArticles((prev) => [...prev, newArticle]);
      showSnackbar("Artículo agregado correctamente", "success", 3000);
    }
    handleCloseModal();
  };

  const articleColumns = [
    {
      header: "Código Producto",
      key: "code",
    },
    {
      header: "Nombre",
      key: "nombre",
    },
    {
      header: "Cantidad",
      key: "cantidad",
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: ArticleItem) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditArticle(row)}
            sx={{ mr: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteArticle(row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Form Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/stock")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Stocks
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Ingresar Stock
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Ingresar Stock
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete la información del nuevo stock
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* Form Fields */}
              <Box sx={{ flex: 1 }}>
                <Stack spacing={3}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="estimatedArribalDate"
                        control={control}
                        rules={{
                          required: "La fecha estimada de llegada es requerida",
                        }}
                        render={({ field }) => (
                          <Input
                            label="Fecha estimada de llegada"
                            name="estimatedArribalDate"
                            field={field}
                            required
                            type="date"
                            error={errors.estimatedArribalDate?.message}
                            icon={<CalendarTodayIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="code"
                        control={control}
                        rules={{
                          required: "El código interno es requerido",
                        }}
                        render={({ field }) => (
                          <Input
                            label="Código Interno"
                            name="code"
                            field={field}
                            required
                            error={errors.code?.message}
                            icon={<QrCodeIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "end",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="companyId"
                        control={control}
                        rules={{
                          required: "La sociedad es requerida",
                        }}
                        render={({ field: { onChange, value } }) => (
                          <Autocomplete
                            fullWidth
                            open={societyOpen}
                            onOpen={() => setSocietyOpen(true)}
                            onClose={() => setSocietyOpen(false)}
                            options={societies}
                            getOptionLabel={(option) => option.name}
                            loading={loadingSocieties}
                            inputValue={societyInputValue}
                            value={
                              societies.find(
                                (society) => society.companyId === value,
                              ) || null
                            }
                            onChange={(_, newValue) => {
                              onChange(newValue?.companyId || "");
                              // Actualizar el inputValue con el nombre de la sociedad seleccionada
                              setSocietyInputValue(newValue?.name || "");
                            }}
                            onInputChange={(_, newInputValue, reason) => {
                              // Actualizar el input value para búsqueda en tiempo real
                              if (reason === "input") {
                                setSocietyInputValue(newInputValue);
                              }
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.companyId === value.companyId
                            }
                            noOptionsText="No se encontraron sociedades"
                            loadingText="Buscando sociedades..."
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sociedad"
                                required
                                error={!!errors.companyId}
                                helperText={errors.companyId?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingSocieties ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="percentageSafety"
                        control={control}
                        rules={{
                          required: "El porcentaje de fallo es requerido",
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Debe ser un número entero válido",
                          },
                          validate: (value) => {
                            const num = parseInt(value);
                            if (num < 0 || num > 100) {
                              return "El porcentaje debe estar entre 0 y 100";
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Porcentaje de fallo"
                            name="percentageSafety"
                            field={field}
                            required
                            type="number"
                            error={errors.percentageSafety?.message}
                            icon={<PercentIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Controller
                      name="observation"
                      control={control}
                      rules={{
                        required: "La observación es requerida",
                      }}
                      render={({ field }) => (
                        <Input
                          label="Observación"
                          name="observation"
                          field={field}
                          required
                          multiline
                          rows={4}
                          error={errors.observation?.message}
                          icon={<NotesIcon />}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Articles Section */}
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6" component="h2">
                  Listado de productos
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenModal}
                >
                  Agregar
                </Button>
              </Box>

              <DataTable
                columns={articleColumns}
                data={articles}
                page={articlePage}
                isLoading={false}
                onChange={(page) => setArticlePage(page)}
                total={articles.length}
              />
            </Box>

            {/* Article Modal */}
            <Dialog
              open={openModal}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {editingArticle ? "Editar Producto" : "Agregar Producto"}
              </DialogTitle>
              <DialogContent>
                <Box
                  component="form"
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <Controller
                    name="nombre"
                    control={articleControl}
                    rules={{
                      required: "El nombre del producto es requerido",
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        fullWidth
                        open={productOpen}
                        onOpen={() => setProductOpen(true)}
                        onClose={() => setProductOpen(false)}
                        options={products}
                        loading={loadingProducts}
                        inputValue={productInputValue}
                        value={
                          products.find(
                            (product) => product.description === value,
                          ) || null
                        }
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : `${option.code} - ${option.description}`
                        }
                        onChange={(_, newValue) => {
                          onChange(newValue?.description || "");
                          setProductInputValue(newValue?.description || "");
                          setArticleValue(
                            "productId",
                            newValue?.productID || "",
                            {
                              shouldValidate: true,
                            },
                          );
                          setArticleValue("code", newValue?.code || "", {
                            shouldValidate: true,
                          });
                        }}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason === "input") {
                            setProductInputValue(newInputValue);
                          }
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.productID === value.productID
                        }
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              fontFamily: "monospace",
                            }}
                          >
                            {/* Código con ancho fijo */}
                            <Box
                              sx={{
                                width: CODE_WIDTH,
                                textAlign: "right",
                                pr: 1,
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                              }}
                            >
                              {option.code}
                            </Box>

                            {/* Separador */}
                            <Box sx={{ pr: 1 }}>-</Box>

                            {/* Descripción */}
                            <Box sx={{ flex: 1, whiteSpace: "nowrap" }}>
                              {option.description}
                            </Box>
                          </Box>
                        )}
                        noOptionsText="No se encontraron productos"
                        loadingText="Buscando productos..."
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Nombre del Producto"
                            required
                            error={
                              !!articleErrors.nombre ||
                              !!articleErrors.productId
                            }
                            helperText={
                              articleErrors.nombre?.message ||
                              articleErrors.productId?.message
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingProducts ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  />

                  {/* Campo oculto para productId */}
                  <Controller
                    name="code"
                    control={articleControl}
                    render={({ field }) => <input type="hidden" {...field} />}
                  />

                  <Controller
                    name="productId"
                    control={articleControl}
                    rules={{
                      required: "Debe seleccionar un producto de la lista",
                    }}
                    render={({ field }) => <input type="hidden" {...field} />}
                  />

                  <Controller
                    name="cantidad"
                    control={articleControl}
                    rules={{
                      required: "La cantidad es requerida",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Debe ser un número válido",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        label="Cantidad"
                        name="cantidad"
                        field={field}
                        required
                        type="number"
                        error={articleErrors.cantidad?.message}
                        icon={<NumbersIcon />}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCloseModal} variant="outlined">
                  Cancelar
                </Button>
                <Button
                  onClick={handleArticleSubmit(onArticleSubmit)}
                  variant="contained"
                >
                  {editingArticle ? "Actualizar" : "Agregar"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 120 }}
                disabled={isLoading}
                loading={isLoading}
              >
                Ingresar Stock
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockCreatePage;
